import React, { useState } from "react";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from "@mui/material";

import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PaymentIcon from "@mui/icons-material/Payment";
import SettingsIcon from "@mui/icons-material/Settings";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";

import {
  GENERATE_ORDER_TEMPLATE,
  UPLOAD_ORDER_PAYMENTS_EXCEL_FILE,
  GENERATE_ORDER_PAYMENTS_TEMPLATE,
  UPLOAD_ORDER_EXCEL_FILE,
  UPLOAD_CONDITIONS_EXCEL_FILE,
  GENERATE_CONDITIONS_TEMPLATE
} from "../../../API/APIRoutes";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import PaymentRoundedIcon from "@mui/icons-material/PaymentRounded";
import SettingsApplicationsRoundedIcon from "@mui/icons-material/SettingsApplicationsRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { useDispatch } from "react-redux";
import { setSnackbar } from "@Redux/snackBarSlice";
import themeCreator from "@Themes/themeCreator";
import PageTitle from "@Components/PageTitle/PageTitle";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import axiosInstance from "@API/axios";

function BulkInsert() {
  const theme = themeCreator();
  const dispatch = useDispatch();

  // State for popup management
  const [openPopup, setOpenPopup] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [currentAction, setCurrentAction] = useState(null); // 'generate' or 'import'
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Section configurations
  const sections = [
    {
      id: "orders",
      title: "Insert Orders",
      description: "Bulk insert orders using Excel templates",
      icon: ShoppingCartRoundedIcon,
      color: "primary",
      gradientColors: ["#1976d2", "#42a5f5"],
    },
    {
      id: "payments",
      title: "Insert Payments",
      description: "Bulk insert payment records using Excel templates",
      icon: PaymentRoundedIcon,
      color: "success",
      gradientColors: ["#1976d2", "#42a5f5"],
    },
    {
      id: "conditions",
      title: "Insert Conditions",
      description: "Bulk insert conditions using Excel templates",
      icon: SettingsApplicationsRoundedIcon,
      color: "warning",
      gradientColors: ["#1976d2", "#42a5f5"],
    },
  ];
  const downloadCSV = (sectionId) => {
    setIsLoading(true);

    // Determine the export URL based on the order state
    let exportUrl;
    switch (sectionId) {
      case "orders":
        exportUrl = GENERATE_ORDER_TEMPLATE;
        break;
      case "payments":
        exportUrl = GENERATE_ORDER_PAYMENTS_TEMPLATE;
        break;
      case "conditions":
        exportUrl = GENERATE_CONDITIONS_TEMPLATE;
        break;
      default:
        exportUrl = GENERATE_ORDER_TEMPLATE; // Default case
    }

    return axiosInstance
      .get(exportUrl, { responseType: "blob" })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.setAttribute("download", `${sectionId}-template.xlsx`);
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
        // Set downloading state to false to hide snackbar
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Excel download failed:", error);
        // Set downloading state to false in case of error
        setIsLoading(false);
      });
  };

  const handleGenerateTemplate = (sectionId) => {
    setCurrentSection(sectionId);
    setCurrentAction("generate");

    downloadCSV(sectionId);
  };

  const handleImportFile = (sectionId) => {
    setCurrentSection(sectionId);
    setCurrentAction("import");
    setOpenPopup(true);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];

      if (!validTypes.includes(file.type)) {
        dispatch(
          setSnackbar({
            open: true,
            message: "Only Excel files (.xlsx, .xls) are allowed.",
            error: true,
          })
        );
        event.target.value = null;
        return;
      }

      setSelectedFile(file);
      setFileName(file.name);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;

    setIsLoading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    let url;
    switch (currentSection) {
      case "orders":
        url = UPLOAD_ORDER_EXCEL_FILE;
        break;
      case "payments":
        url = UPLOAD_ORDER_PAYMENTS_EXCEL_FILE;
        break;
      case "conditions":
        url = UPLOAD_CONDITIONS_EXCEL_FILE;
        break;
      default:
        url = GENERATE_ORDER_TEMPLATE; // Default case
    }
    try {
       await axiosInstance.post(
        `${url}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      dispatch(
        setSnackbar({
          open: true,
          message: `${
            sections.find((s) => s.id === currentSection)?.title
          } imported successfully!`,
          error: false,
        })
      );
      handleClosePopup();
    } catch (error) {
      console.error("Upload failed:", error);
      dispatch(
        setSnackbar({
          open: true,
          message: `Failed to import ${
            sections.find((s) => s.id === currentSection)?.title
          }`,
          error: true,
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClosePopup = () => {
    setOpenPopup(false);
    setSelectedFile(null);
    setFileName("");
    setCurrentSection(null);
    setCurrentAction(null);
  };

  const renderSectionCard = (section) => (
    <Grid item xs={12} md={4} key={section.id}>
      <Card
        elevation={0}
        sx={{
          height: "100%",
          backgroundColor: "white",
          border: "3px solid #000000",
          borderRadius: 3,
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            transform: "translateY(-8px)",
            boxShadow: "0 12px 24px rgba(0,0,0,0.25)",
            borderColor: "#333333",
          },
        }}
      >
        <CardContent
          sx={{
            p: 3,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Box
              sx={{
                backgroundColor: section.gradientColors[0],
                borderRadius: "50%",
                p: 1.5,
                mr: 2,
                color: "white",
              }}
            >
              <section.icon fontSize="large" />
            </Box>
            <Typography variant="h5" fontWeight="bold" color="#000000">
              {section.title}
            </Typography>
          </Box>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{ mb: 3, color: "#333333", flexGrow: 1 }}
          >
            {section.description}
          </Typography>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<DownloadRoundedIcon />}
              onClick={() => handleGenerateTemplate(section.id)}
              disabled={
                isLoading &&
                currentSection === section.id &&
                currentAction === "generate"
              }
              sx={{
                backgroundColor: section.gradientColors[0],
                color: "white",
                fontWeight: "bold",
                border: "2px solid #000000",
                "&:hover": {
                  backgroundColor: section.gradientColors[1],
                  borderColor: "#333333",
                },
                "&:disabled": {
                  backgroundColor: "#cccccc",
                  borderColor: "#999999",
                },
              }}
            >
              {isLoading &&
              currentSection === section.id &&
              currentAction === "generate" ? (
                <LoadingComponent size="1.2em" thickness={5} color="white" />
              ) : (
                "Generate Template"
              )}
            </Button>

            <Button
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              onClick={() => handleImportFile(section.id)}
              sx={{
                backgroundColor: "white",
                color: section.gradientColors[0],
                fontWeight: "bold",
                border: `2px solid ${section.gradientColors[0]}`,
              }}
            >
              Import Excel File
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <PageTitle
          title="Bulk Insert Operations"
          PageIcon={<CategoryRoundedIcon color="white" fontSize="large" />}
          iconColor={theme.palette.primary.main}
        />
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ mt: 2, textAlign: "center" }}
        >
          Efficiently manage bulk data operations with Excel templates
        </Typography>
      </Box>

      {/* Sections Grid */}
      <Grid container spacing={4}>
        {sections.map(renderSectionCard)}
      </Grid>

      {/* Import Popup */}
      <Dialog
        open={openPopup}
        onClose={handleClosePopup}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: currentSection
              ? `linear-gradient(135deg, ${
                  sections.find((s) => s.id === currentSection)
                    ?.gradientColors[0]
                } 0%, ${
                  sections.find((s) => s.id === currentSection)
                    ?.gradientColors[1]
                } 100%)`
              : theme.palette.primary.main,
            color: "white",
            textAlign: "center",
            fontWeight: 600,
            py: 3,
          }}
        >
          Import {sections.find((s) => s.id === currentSection)?.title}
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography variant="h6" color="text.primary" sx={{ mb: 2 }}>
              Upload Excel File
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Select an Excel file (.xlsx, .xls) to import{" "}
              {sections
                .find((s) => s.id === currentSection)
                ?.title.toLowerCase()}
            </Typography>

            <Paper
              elevation={0}
              sx={{
                border: `2px dashed ${theme.palette.primary.light}`,
                borderRadius: 2,
                p: 4,
                backgroundColor: theme.palette.background.default,
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: theme.palette.primary.light + "10",
                },
              }}
              component="label"
            >
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleFileChange}
              />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CloudUploadRoundedIcon
                  sx={{
                    fontSize: 48,
                    color: selectedFile
                      ? theme.palette.success.main
                      : theme.palette.primary.main,
                  }}
                />
                <Typography variant="h6" color="primary">
                  {selectedFile ? "Change File" : "Click to Upload File"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supported formats: .xlsx, .xls
                </Typography>
              </Box>
            </Paper>

            {fileName && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: theme.palette.success.light + "20",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `1px solid ${theme.palette.success.light}`,
                }}
              >
                <AttachFileIcon
                  fontSize="small"
                  color="success"
                  sx={{ mr: 1 }}
                />
                <Typography
                  variant="body1"
                  color="success.main"
                  fontWeight="medium"
                >
                  {fileName}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleClosePopup}
            variant="outlined"
            size="large"
            sx={{ minWidth: 120 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="large"
            disabled={!selectedFile || isLoading}
            sx={{ minWidth: 120 }}
          >
            {isLoading ? (
              <LoadingComponent size="1.5em" thickness={5} color="white" />
            ) : (
              "Submit"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default BulkInsert;
